import { createApi } from '@reduxjs/toolkit/query/react';
import moment from 'moment';
import baseQueryWithTokenManagement from 'pages/auth/apiSlice';
import { convertStringsToOptionsObject } from '../utils/helpers';
import { purchaseRequestApi } from 'api/purchaseRequest';

export const leadsApi = createApi({
  tagTypes: ['LEAD', 'LEAD_LIST', 'LEAD_STATUS', 'LEAD_SOURCE'],
  reducerPath: 'leadsApi',
  baseQuery: baseQueryWithTokenManagement,
  endpoints: (builder) => ({
    leadList: builder.query<any, any>({
      query: (args) => {
        const { size = 10, page = 1, sortField, forDashboard, sortOrder, search, filters } = args;
        const number = page - 1;
        let sortParam = '';
        if (sortField && sortOrder) {
          sortParam = `&sort=${sortField},${sortOrder}`;
        }
        const params = { ...filters, ...(typeof search !== 'undefined' && { search }) };
        if (forDashboard) {
          params.forDashboard = true;
          params.assignment = JSON.parse(localStorage.getItem('userId') || '');
        }
        return {
          url: `/wrd/api/lead/list?size=${size}&page=${number}${sortParam}`,
          params: Object.keys(params).length ? params : undefined,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data.content = (response?.data?.content || []).map((item: any) => ({
          ...item,
          formattedLastModifiedDate: moment(item.lastModifiedDate).format('DD.MM.YYYY'),
          formattedCreateDate: moment(item.createDate).format('DD.MM.YYYY'),
          key: item.id,
        }));
        return response;
      },
      providesTags: () => ['LEAD_LIST'],
    }),
    leadById: builder.query<any, { id: string }>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/lead/${id}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return {
          ...response.data[0],
          isFinalStatus: ['REJECTED', 'CONVERTED'].includes(response.data[0].status),
          formattedLastModifiedDate: moment(response.data[0].lastModifiedDate).format('DD.MM.YYYY HH:mm:ss'),
          formattedCreatedDate: moment(response.data[0].createDate).format('DD.MM.YYYY HH:mm:ss'),
        };
      },
      providesTags: () => ['LEAD'],
    }),
    leadStatusTypes: builder.query<any, void>({
      query: () => {
        return {
          url: `/wrd/api/lead/lead-status-types`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data = convertStringsToOptionsObject(response.data);
        return response.data;
      },
      providesTags: () => ['LEAD_STATUS'],
    }),
    leadStatusChange: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/lead/change-status',
        method: 'put',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch({
            type: 'dealsApi/invalidateTags',
            payload: ['DEAL_LIST'],
          });
          dispatch(purchaseRequestApi.util.invalidateTags(['PURCHASE_REQUEST_LIST']));
        } catch {
          // Handle error if needed
        }
      },
      invalidatesTags: () => ['LEAD', 'LEAD_LIST', 'LEAD_STATUS'],
    }),
    rejectLeadBrand: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/lead/reject-brand',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['LEAD', 'LEAD_LIST', 'LEAD_STATUS'],
    }),
    removeLeadBrand: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/lead/remove-brand',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['LEAD', 'LEAD_LIST', 'LEAD_STATUS'],
    }),
    leadSourceTypes: builder.query<any, void>({
      query: () => {
        return {
          url: `/wrd/api/lead/lead-source-types`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data = convertStringsToOptionsObject(response.data);
        return response.data;
      },
      providesTags: () => ['LEAD_SOURCE'],
    }),
    createLead: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/lead',
        method: 'post',
        body: data,
      }),
      invalidatesTags: () => ['LEAD_LIST'],
    }),
    updateLead: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/lead',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['LEAD', 'LEAD_LIST'],
    }),
    updateLeadAssignment: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/lead/assignments',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['LEAD', 'LEAD_LIST'],
    }),
    uploadEmail: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/email/upload',
        method: 'post',
        body: data,
      }),
      invalidatesTags: () => ['LEAD'],
    }),
    getEmail: builder.query<any, { id: string }>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/email/${id}`,
        };
      },
    }),
    updateLeadItem: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/lead/update-item',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['LEAD'],
    }),
    addLeadItem: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/lead/add-item',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['LEAD'],
    }),
    removeLeadBrandItem: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/lead/remove-item',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['LEAD'],
    }),
  }),
});

export const {
  useLeadListQuery,
  useLeadByIdQuery,
  useCreateLeadMutation,
  useUpdateLeadMutation,
  useUpdateLeadAssignmentMutation,
  useUploadEmailMutation,
  useLeadStatusTypesQuery,
  useLeadStatusChangeMutation,
  useLeadSourceTypesQuery,
  useGetEmailQuery,
  useRejectLeadBrandMutation,
  useRemoveLeadBrandMutation,
  useUpdateLeadItemMutation,
  useAddLeadItemMutation,
  useRemoveLeadBrandItemMutation,
} = leadsApi;
